import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient, useLazyQuery } from "@apollo/client/react";
import {
  MapAddressDetailsDocument,
  MapInfoboxIssuesDocument,
  type MapAddressDetailsRow,
} from "#app/lib/data";

import type { MapPin } from "#app/components/map/map-types";
import type { MapIssue } from "#app/components/map/map-issue";

const PROPERTY_DETAILS_POLL_MS = 750;
const PROPERTY_DETAILS_POLL_TIMEOUT_MS = 15_000;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AddressDetails = MapAddressDetailsRow;

// ---------------------------------------------------------------------------
// Prefetch — the hover before the click
// ---------------------------------------------------------------------------

/**
 * Warms the infobox's two queries for a pin the pointer is resting on, so the
 * click that follows opens on cached cards and a cached address instead of
 * skeletons that the answers then reshape. Same documents and variables as
 * {@link useMapInfoboxData}, so its `cache-and-network` read lands on the
 * warmed entries; `cache-first` here so a pin hovered twice costs one trip.
 * Fire-and-forget: a failure surfaces on the real read, not on a hover.
 */
export function usePrefetchMapInfoboxData(): (pin: MapPin) => void {
  const client = useApolloClient();
  return useCallback(
    (pin: MapPin) => {
      if (pin.issueIds.length > 0) {
        void client
          .query({
            query: MapInfoboxIssuesDocument,
            variables: { issueIds: pin.issueIds },
            fetchPolicy: "cache-first",
          })
          .catch(() => {});
      }
      if (pin.addressId) {
        void client
          .query({
            query: MapAddressDetailsDocument,
            variables: { addressId: pin.addressId },
            fetchPolicy: "cache-first",
          })
          .catch(() => {});
      }
    },
    [client],
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useMapInfoboxData(pin: MapPin | null) {
  const [fetchIssues, { data: issuesData }] = useLazyQuery(
    MapInfoboxIssuesDocument,
    {
      fetchPolicy: "cache-and-network",
    },
  );

  const [
    fetchAddress,
    { data: addressData, refetch: refetchAddress },
  ] = useLazyQuery(MapAddressDetailsDocument, {
    fetchPolicy: "cache-and-network",
  });

  const [timedOutPlacementId, setTimedOutPlacementId] = useState<
    string | null
  >(null);

  const pinId = pin?.id;
  const addressId = pin?.addressId;
  const isEmptyLocalPin = Boolean(pin?.isLocal && pin.issueIds.length === 0);
  const propertyDetails =
    addressData?.address?.propertyDetails ?? pin?.propertyDetails ?? null;

  useEffect(() => {
    if (!pin) return;

    if (pin.issueIds.length > 0) {
      fetchIssues({ variables: { issueIds: pin.issueIds } });
    }
  }, [pin, fetchIssues]);

  useEffect(() => {
    if (!addressId) return;

    let canceled = false;
    let retryTimeout: number | undefined;
    const expiresAt = Date.now() + PROPERTY_DETAILS_POLL_TIMEOUT_MS;
    const shouldRetry = Boolean(
      pinId && isEmptyLocalPin && !pin?.propertyDetails,
    );

    const fetchLatestAddress = async (isRetry: boolean) => {
      try {
        const result = isRetry
          ? await refetchAddress({ addressId })
          : await fetchAddress({ variables: { addressId } });
        if (canceled || result.data?.address?.propertyDetails) return;
      } catch {
        if (canceled) return;
      }

      if (!shouldRetry) return;
      if (Date.now() >= expiresAt) {
        setTimedOutPlacementId(pinId ?? null);
        return;
      }
      retryTimeout = window.setTimeout(() => {
        void fetchLatestAddress(true);
      }, PROPERTY_DETAILS_POLL_MS);
    };

    void fetchLatestAddress(false);

    return () => {
      canceled = true;
      if (retryTimeout !== undefined) window.clearTimeout(retryTimeout);
    };
  }, [
    addressId,
    fetchAddress,
    isEmptyLocalPin,
    pin?.propertyDetails,
    pinId,
    refetchAddress,
  ]);

  const issuesDataRef = useRef(issuesData);
  const issuesRef = useRef<MapIssue[]>([]);
  const nextIssues: MapIssue[] = useMemo(
    () =>
      issuesData?.issues?.edges?.map(
        (e: { node: MapIssue }) => e.node,
      ) ?? [],
    [issuesData],
  );

  // Update issues when Apollo data reference changes.
  if (issuesDataRef.current !== issuesData) {
    issuesDataRef.current = issuesData;
    issuesRef.current = nextIssues;
  }
  const issues = issuesRef.current;

  const addressDetails: AddressDetails | null =
    addressData?.address ?? null;
  const propertyDetailsLoading = Boolean(
    isEmptyLocalPin &&
      !propertyDetails &&
      (!pinId || timedOutPlacementId !== pinId),
  );

  return {
    issues,
    addressDetails,
    propertyDetailsLoading,
  };
}
